import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeOffIcon } from '../components/Icons';
import { AdminUserApi, CompanyApi, DepositApi, SubscriptionApi, UserApi } from '../api';
import type { SubscriptionDto } from '../api';
import { Deposit } from '../types/Product';
import EmailUpdateModal from '../components/EmailUpdateModal';

// ---- Types ----
interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  firstLogin?: boolean;
  active?: boolean;
}
type Company = { id: number; name: string; location?: string };

// Small chip for assigned deposits
function DepositChip({
  deposit,
  onRemove,
  t,
}: {
  deposit: Deposit;
  onRemove?: (id: number) => void;
  t: (key: string) => string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#e8f5e9',
        color: '#2e7d32',
        border: '1px solid #c8e6c9',
        borderRadius: 16,
        padding: '2px 10px',
        marginRight: 6,
        marginBottom: 6,
        fontSize: 12,
        whiteSpace: 'nowrap',
      }}
    >
      {deposit.name}
      {onRemove && (
        <button
          onClick={() => onRemove(deposit.id)}
          title={t('user.removeDepositAccess')}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2e7d32',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

const rolesList = ['ROLE_ADMIN', 'ROLE_WAREHOUSE', 'ROLE_INVENTORY', 'ROLE_TRANSPORT', 'ROLE_VIEWER'];

// Fixed subscription plans matching backend SubscriptionPlan enum
const SUBSCRIPTION_PLANS: Array<{
  key: string;
  label: string;
  period: string;
  days: number | null;
  color: string;
  bg: string;
  description: string;
}> = [
  { key: 'FREE',       label: 'Free Trial',  period: '30 days',  days: 30,   color: '#546e7a', bg: '#eceff1', description: '30-day free trial' },
  { key: 'TRIAL',      label: 'Trial',       period: '3 months', days: 90,   color: '#00796b', bg: '#e0f2f1', description: 'Extended 90-day trial' },
  { key: 'BASIC',      label: 'Monthly',     period: '1 month',  days: 30,   color: '#1565c0', bg: '#e3f2fd', description: 'Monthly plan – 30 days (€50)' },
  { key: 'PREMIUM',    label: 'Premium',     period: '6 months', days: 180,  color: '#6a1b9a', bg: '#f3e5f5', description: 'Premium plan – 180 days' },
  { key: 'ENTERPRISE', label: 'Enterprise',  period: '1 year',   days: 365,  color: '#b71c1c', bg: '#ffebee', description: 'Enterprise – 365 days' },
  { key: 'LIFETIME',   label: 'Lifetime',    period: '∞',        days: null, color: '#e65100', bg: '#fff3e0', description: 'Lifetime subscription' },
];

// Small chip for assigned companies
function CompanyChip({
  company,
  onRemove,
  t,
}: {
  company: Company;
  onRemove?: (id: number) => void;
  t: (key: string) => string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#e3f2fd',
        color: '#1976d2',
        border: '1px solid #cfe0ff',
        borderRadius: 16,
        padding: '2px 10px',
        marginRight: 6,
        marginBottom: 6,
        fontSize: 12,
        whiteSpace: 'nowrap',
      }}
    >
      {company.name}
      {onRemove && (
        <button
          onClick={() => onRemove(company.id)}
          title={t('user.removeAccess')}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#1976d2',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

// Key icon SVG (from your code)
function KeyIcon({ style = {} }) {
  return (
    <svg
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="7" cy="17" r="3" />
      <path d="M21 2l-9.6 9.6" />
      <path d="M15 8l-4 4" />
      <path d="M17 10l-4 4" />
    </svg>
  );
}

export default function UserAdminPage() {
  const { t } = useTranslation();
  const roleLabel = (value: string) => {
    const labels: Record<string, string> = {
      ROLE_ADMIN: t('user.admin'),
      ROLE_WAREHOUSE: t('user.warehouse'),
      ROLE_INVENTORY: t('user.inventoryOperator'),
      ROLE_TRANSPORT: t('user.transportManager'),
      ROLE_VIEWER: t('user.viewer'),
    };
    return labels[value] || value;
  };
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('ROLE_VIEWER');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Modal states
  const [showEmailUpdateModal, setShowEmailUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // Admin password reset modal
  const [showAdminPassModal, setShowAdminPassModal] = useState(false);
  const [adminPassUser, setAdminPassUser] = useState<User | null>(null);
  const [adminPassValue, setAdminPassValue] = useState('');
  const [adminPassVisible, setAdminPassVisible] = useState(false);
  const [adminPassLoading, setAdminPassLoading] = useState(false);
  const [adminPassError, setAdminPassError] = useState('');
  // Result modal
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showGeneratedPasswordModal, setShowGeneratedPasswordModal] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  // Assigned companies cache: userId -> Company[]
  const [assignedByUser, setAssignedByUser] = useState<Record<number, Company[]>>({});
  // Assigned deposits cache: userId -> Deposit[]
  const [assignedDepositsByUser, setAssignedDepositsByUser] = useState<Record<number, Deposit[]>>({});
  // All deposits (admin view)
  const [allDeposits, setAllDeposits] = useState<Deposit[]>([]);
  // Panel state: which user is expanded for management
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  // Per-row select state (company to add)
  const [rowSelect, setRowSelect] = useState<Record<number, number | ''>>({});
  // Per-row select state (deposit to add)
  const [rowDepositSelect, setRowDepositSelect] = useState<Record<number, number | ''>>({});
  // Per-row selected company context for deposit assignment
  const [rowDepositCompanySelect, setRowDepositCompanySelect] = useState<Record<number, number | ''>>({});
  // Busy flags to avoid duplicate clicks
  const [busyUserIds, setBusyUserIds] = useState<Record<number, boolean>>({});

  // Subscription management
  const [subscriptionsByUser, setSubscriptionsByUser] = useState<Record<number, SubscriptionDto | null>>({});
  const [subscriptionLoadedUsers, setSubscriptionLoadedUsers] = useState<Set<number>>(new Set());
  const [subVersions, setSubVersions] = useState<Record<number, number>>({});
  const [showSubscriptionPanel, setShowSubscriptionPanel] = useState<number | null>(null);
  const [daysInput, setDaysInput] = useState<Record<number, string>>({});
  const [highlightedUsers, setHighlightedUsers] = useState<Set<number>>(new Set());
  // State for new subscription creation form per user
  const [newSubForm, setNewSubForm] = useState<Record<number, { plan: string; autoRenew: boolean }>>({});

  const patchSub = (userId: number, patch: Partial<SubscriptionDto>) => {
    setSubscriptionsByUser(prev => {
      const existing = prev[userId];
      if (!existing) return prev;
      return { ...prev, [userId]: { ...existing, ...patch } };
    });
    setSubVersions(prev => ({ ...prev, [userId]: (prev[userId] ?? 0) + 1 }));
  };

  // Generate random password (from your code)
  function generatePassword(length = 10) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let pass = '';
    for (let i = 0; i < length; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  }
  const openAdminPassModal = (user: User) => {
    const pre = generatePassword(12);
    setAdminPassUser(user);
    setAdminPassValue(pre);
    setAdminPassVisible(false);
    setAdminPassError('');
    setShowAdminPassModal(true);
  };

  const submitAdminResetPassword = async () => {
    if (!adminPassUser) return;
    if (adminPassValue.length < 6) {
      setAdminPassError('Password must be at least 6 characters');
      return;
    }
    setAdminPassLoading(true);
    setAdminPassError('');
    try {
      const result = await UserApi.adminResetPassword(adminPassUser.id, adminPassValue);
      setShowAdminPassModal(false);
      setGeneratedPassword(result.generatedPassword);
      setCopiedPassword(false);
      setShowGeneratedPasswordModal(true);
    } catch (err: any) {
      setAdminPassError(err?.response?.data?.message || 'Failed to reset password');
    } finally {
      setAdminPassLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchDeposits();
    // Administration data is loaded once on entry and refreshed after each action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await AdminUserApi.getAll();
      const fetchedUsers = res.data || [];
      setUsers(fetchedUsers);

      // Prime assignments upfront so companies/deposits are already visible on first open.
      await Promise.all(
        fetchedUsers.map(async (u) => {
          await Promise.all([loadUserCompanies(u.id), loadUserDeposits(u.id)]);
        })
      );
    } catch {
      setError(t('userManagement.failedToFetchUsers'));
    }
  };

  const fetchCompanies = async () => {
    try {
      // Use admin endpoint to get ALL companies for management purposes
      const res = await CompanyApi.getAllForAdmin();
      setCompanies(res.data);
    } catch {
      // silently ignore; UI will still allow manual assign if list is empty
      setCompanies([]);
    }
  };

  const fetchDeposits = async () => {
    try {
      // Use admin endpoint to get ALL deposits for management purposes
      const res = await DepositApi.getAllDepositsForAdmin();
      setDeposits(res.data);
      setAllDeposits(res.data);
    } catch {
      // silently ignore; UI will still allow manual assign if list is empty
      setDeposits([]);
      setAllDeposits([]);
    }
  };

  // Load assigned deposits for a user
  const loadUserDeposits = async (userId: number) => {
    try {
      const res = await DepositApi.getDepositsByUser(userId);
      console.log('Loaded deposits for user', userId, ':', res.data); // Debug log
      setAssignedDepositsByUser(prev => ({ ...prev, [userId]: res.data || [] }));
    } catch (err) {
      console.error('Failed to load user deposits:', err);
      // Fallback: if the endpoint is not available yet, show empty and let admin assign
      setAssignedDepositsByUser(prev => ({ ...prev, [userId]: prev[userId] ?? [] }));
    }
  };

  // Load assigned companies for a user (if backend supports it)
  const loadUserCompanies = async (userId: number) => {
    try {
      // Use CompanyApi to match the Spring Boot endpoint /companies/{userId}
      const res = await CompanyApi.userCompanies(userId);
      console.log('Loaded companies for user', userId, ':', res.data); // Debug log
      const list = res.data || [];
      setAssignedByUser(prev => ({ ...prev, [userId]: list }));
      setRowDepositCompanySelect(prev => ({
        ...prev,
        [userId]: (typeof prev[userId] === 'number' && list.some((c: Company) => c.id === prev[userId]))
          ? prev[userId]
          : (list[0]?.id ?? ''),
      }));
    } catch (err) {
      console.error('Failed to load user companies:', err);
      // Fallback: if the endpoint is not available yet, show empty and let admin assign
      setAssignedByUser(prev => ({ ...prev, [userId]: prev[userId] ?? [] }));
      setRowDepositCompanySelect(prev => ({ ...prev, [userId]: prev[userId] ?? '' }));
    }
  };

  const getDepositsForSelectedCompany = (userId: number) => {
    const selectedCompanyId = rowDepositCompanySelect[userId];
    if (!selectedCompanyId || typeof selectedCompanyId !== 'number') return [];

    return allDeposits.filter(d => d.company?.id === selectedCompanyId);
  };

  const handleCreateUser = async () => {
    setError('');
    setSuccess('');
    if (!username || !password || !email) {
      setError(t('userManagement.usernameEmailPasswordRequired'));
      return;
    }
    try {
      await AdminUserApi.create({ 
        username, 
        email, 
        password, 
        role,
        firstLogin: true // Force password change on first login
      });
      setSuccess(t('userManagement.userCreated'));
      setUsername('');
      setEmail('');
      setPassword('');
      setRole('ROLE_VIEWER');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data || t('userManagement.failedToCreateUser'));
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    setSuccess('');
    try {
      await AdminUserApi.delete(id);
      setSuccess('User deleted');
      setUsers(prev => prev.filter(u => u.id !== id));
      // also clear cache for this user
      setAssignedByUser(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err: any) {
      setError(err.response?.data || t('userManagement.failedToDeleteUser'));
    }
  };

  const handleChangeRole = async (id: number, newRole: string) => {
    setError('');
    setSuccess('');
    try {
      await AdminUserApi.updateRole(id, newRole);
      setSuccess('Role updated');
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, role: newRole } : u)));
    } catch (err: any) {
      setError(err.response?.data || t('userManagement.failedToUpdateRole'));
    }
  };

  const handleToggleActive = async (id: number, currentActive: boolean) => {
    setError('');
    setSuccess('');
    try {
      const res = await AdminUserApi.toggleActive(id);
      setUsers(prev => prev.map(u => (u.id === id ? { ...u, active: res.data.active } : u)));
      setSuccess(res.data.active ? 'User activated' : 'User deactivated');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to toggle user status');
    }
  };

  // Expand / collapse the manage panel
  const toggleManageCompanies = async (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    // lazy-load assigned companies and deposits once expanded
    if (!assignedByUser[userId]) {
      await loadUserCompanies(userId);
    }
    if (!assignedDepositsByUser[userId]) {
      await loadUserDeposits(userId);
    }
    // ensure we have deposits list
    if (!deposits.length) await fetchDeposits();
  };

  const handleAssignCompany = async (userId: number) => {
    const companyId = rowSelect[userId];
    if (!companyId || typeof companyId !== 'number') return;
    const company = companies.find(c => c.id === companyId);
    if (company) setAssignedByUser(prev => ({ ...prev, [userId]: [...(prev[userId] ?? []), company] }));
    setRowSelect(prev => ({ ...prev, [userId]: '' }));
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      await CompanyApi.assignToUser(userId, companyId);
      await loadUserCompanies(userId); // confirm with real server list
      setSuccess('Company assigned');
    } catch (err: any) {
      if (company) setAssignedByUser(prev => ({ ...prev, [userId]: (prev[userId] ?? []).filter(c => c.id !== companyId) }));
      setError(err.response?.data || t('userManagement.failedToAssignCompany'));
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRemoveCompany = async (userId: number, companyId: number) => {
    const removed = (assignedByUser[userId] ?? []).find(c => c.id === companyId);
    setAssignedByUser(prev => ({ ...prev, [userId]: (prev[userId] ?? []).filter(c => c.id !== companyId) }));
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      await CompanyApi.removeFromUser(userId, companyId);
      setSuccess('Company removed');
    } catch (err: any) {
      if (removed) setAssignedByUser(prev => ({ ...prev, [userId]: [...(prev[userId] ?? []), removed] }));
      setError(err.response?.data || t('userManagement.failedToRemoveCompany'));
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleAssignDeposit = async (userId: number) => {
    const depositId = rowDepositSelect[userId];
    if (!depositId || typeof depositId !== 'number') return;

    const selectedCompanyId = rowDepositCompanySelect[userId];
    if (!selectedCompanyId || typeof selectedCompanyId !== 'number') {
      setError('Please select a company context for deposit assignment');
      return;
    }

    const companyId = selectedCompanyId;

    const deposit = allDeposits.find(d => d.id === depositId);
    if (deposit) setAssignedDepositsByUser(prev => ({ ...prev, [userId]: [...(prev[userId] ?? []), deposit] }));
    setRowDepositSelect(prev => ({ ...prev, [userId]: '' }));
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      await CompanyApi.assignUserToCompanyDeposit(companyId, depositId, userId);
      await loadUserDeposits(userId); // confirm with real server list
      setSuccess('Deposit assigned');
    } catch (err: any) {
      if (deposit) setAssignedDepositsByUser(prev => ({ ...prev, [userId]: (prev[userId] ?? []).filter(d => d.id !== depositId) }));
      setError(err.response?.data || 'Failed to assign deposit');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleRemoveDeposit = async (userId: number, depositId: number) => {
    const deposit = allDeposits.find(d => d.id === depositId);
    const companyId = deposit?.company?.id;
    if (!companyId) {
      setError('Cannot remove deposit: deposit has no company relation');
      return;
    }
    const removed = (assignedDepositsByUser[userId] ?? []).find(d => d.id === depositId);
    setAssignedDepositsByUser(prev => ({ ...prev, [userId]: (prev[userId] ?? []).filter(d => d.id !== depositId) }));
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      await CompanyApi.removeUserFromCompanyDeposit(companyId, depositId, userId);
      setSuccess('Deposit removed');
    } catch (err: any) {
      if (removed) setAssignedDepositsByUser(prev => ({ ...prev, [userId]: [...(prev[userId] ?? []), removed] }));
      setError(err.response?.data || 'Failed to remove deposit');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Subscription management functions
  const loadUserSubscription = async (userId: number) => {
    try {
      const res = await SubscriptionApi.getByUserId(userId);
      // 204 No Content → res.data is empty; treat as no subscription
      const data = res.data && (res.data as any).id ? res.data : null;
      setSubscriptionsByUser(prev => ({ ...prev, [userId]: data }));
    } catch (err: any) {
      // 404 or any error → no subscription
      setSubscriptionsByUser(prev => ({ ...prev, [userId]: null }));
    } finally {
      setSubscriptionLoadedUsers(prev => new Set(prev).add(userId));
    }
  };

  const toggleSubscriptionPanel = async (userId: number) => {
    if (showSubscriptionPanel === userId) {
      setShowSubscriptionPanel(null);
      return;
    }
    setShowSubscriptionPanel(userId);
    if (!subscriptionLoadedUsers.has(userId)) {
      await loadUserSubscription(userId);
    }
  };

  const handleCreateSubscription = async (userId: number) => {
    const form = newSubForm[userId] ?? { plan: 'FREE', autoRenew: false };
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await SubscriptionApi.create(userId, form.plan, form.autoRenew);
      setSubscriptionsByUser(prev => ({ ...prev, [userId]: res.data }));
      setSubVersions(prev => ({ ...prev, [userId]: (prev[userId] ?? 0) + 1 }));
      flashHighlight(userId);
      setSuccess('Subscription created successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data || 'Failed to create subscription');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const flashHighlight = (userId: number) => {
    setHighlightedUsers(prev => new Set(prev).add(userId));
    setTimeout(() => setHighlightedUsers(prev => { const s = new Set(prev); s.delete(userId); return s; }), 1200);
  };

  const handleSetInfiniteSubscription = async (userId: number, infinite: boolean) => {
    const previous = subscriptionsByUser[userId];
    patchSub(userId, { infiniteSubscription: infinite });
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await SubscriptionApi.setInfinite(userId, infinite);
      patchSub(userId, res.data);
      flashHighlight(userId);
      setSuccess(infinite ? 'Set to infinite subscription' : 'Removed infinite subscription');
    } catch (err: any) {
      if (previous) patchSub(userId, previous);
      setError(err.response?.data || 'Failed to update subscription');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdatePlan = async (userId: number, plan: string) => {
    const previous = subscriptionsByUser[userId];
    patchSub(userId, { plan: plan as any });
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await SubscriptionApi.updatePlan(userId, plan);
      patchSub(userId, res.data);
      flashHighlight(userId);
      setSuccess('Subscription plan updated');
    } catch (err: any) {
      if (previous) patchSub(userId, previous);
      setError(err.response?.data || 'Failed to update plan');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleCancelSubscription = async (userId: number) => {
    if (!window.confirm('Cancel this user\'s subscription? Their status will be set to CANCELLED.')) return;
    const previous = subscriptionsByUser[userId];
    patchSub(userId, { status: 'CANCELLED' as any });
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await SubscriptionApi.cancel(userId);
      patchSub(userId, res.data);
      flashHighlight(userId);
      setSuccess('Subscription cancelled');
    } catch (err: any) {
      if (previous) patchSub(userId, previous);
      setError(err.response?.data || 'Failed to cancel subscription');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleExtendDays = async (userId: number, delta: number) => {
    if (delta === 0) return;
    const previous = subscriptionsByUser[userId];
    const currentDays = Number(previous?.remainingDays ?? 0);
    const optimisticDays = delta > 0 ? currentDays + delta : Math.max(0, currentDays + delta);
    patchSub(userId, { remainingDays: optimisticDays });
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      let res;
      if (delta > 0) {
        res = await SubscriptionApi.extend(userId, delta);
      } else {
        res = await SubscriptionApi.setDays(userId, optimisticDays);
      }
      patchSub(userId, res.data);
      flashHighlight(userId);
      setSuccess(`Subscription ${delta > 0 ? 'extended' : 'reduced'} by ${Math.abs(delta)} day(s)`);
    } catch (err: any) {
      if (previous) patchSub(userId, previous);
      setError(err.response?.data?.message || err.response?.data || 'Failed to adjust days');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleSetExactDays = async (userId: number) => {
    const target = parseInt(daysInput[userId] || '', 10);
    if (isNaN(target) || target < 0) { setError('Enter a valid number of days (0 or more)'); return; }
    const previous = subscriptionsByUser[userId];
    patchSub(userId, { remainingDays: target, status: (target === 0 ? 'EXPIRED' : 'ACTIVE') as any });
    setBusyUserIds(prev => ({ ...prev, [userId]: true }));
    setError('');
    setSuccess('');
    try {
      const res = await SubscriptionApi.setDays(userId, target);
      patchSub(userId, res.data);
      flashHighlight(userId);
      setSuccess(`Days remaining set to ${target}`);
      setDaysInput(prev => ({ ...prev, [userId]: '' }));
    } catch (err: any) {
      if (previous) patchSub(userId, previous);
      setError(err.response?.data?.message || err.response?.data || 'Failed to set days');
    } finally {
      setBusyUserIds(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Only show if current user is admin (assume role is stored in localStorage)
  const currentRole = localStorage.getItem('role');
  if (currentRole !== 'ROLE_ADMIN') {
    return (
      <div style={{ padding: 32, color: '#000' }}>
        Access denied. Admins only. currentRole: {currentRole}
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <h2 style={{ color: '#1976d2', marginBottom: 24 }}>{t('userManagement.title')}</h2>

      {/* Create user */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ color: '#1976d2' }}>{t('userManagement.createNewUser')}</h3>
        <input
          type="text"
          placeholder={t('common.username')}
          value={username}
          onChange={e => setUsername(e.target.value)}
          style={{ marginRight: 8, padding: 8, borderRadius: 4, border: '1px solid #1976d2' }}
        />
        <input
          type="email"
          placeholder={t('common.email')}
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ marginRight: 8, padding: 8, borderRadius: 4, border: '1px solid #1976d2' }}
        />
        <div style={{ position: 'relative', display: 'inline-block', marginRight: 8 }}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder={t('common.password')}
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={{
              padding: '8px 40px 8px 8px',
              borderRadius: 4,
              border: '1px solid #1976d2',
              width: 180,
            }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              color: '#1976d2',
              border: 'none',
              borderRadius: 6,
              padding: 0,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 20,
              display: 'inline-flex',
              alignItems: 'center',
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeIcon style={{ fontSize: 24 }} /> : <EyeOffIcon style={{ fontSize: 24 }} />}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            const newPass = generatePassword();
            setPassword(newPass);
            setShowPassword(true);
          }}
          style={{
            marginRight: 8,
            background: 'transparent',
            color: '#1976d2',
            border: 'none',
            borderRadius: 6,
            padding: 0,
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 20,
            display: 'inline-flex',
            alignItems: 'center',
          }}
          aria-label="Generate password"
        >
          <KeyIcon style={{ fontSize: 24 }} />
        </button>
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          style={{ padding: 8, borderRadius: 4, border: '1px solid #1976d2', minWidth: 140 }}
        >
          {rolesList.map(roleOpt => (
            <option key={roleOpt} value={roleOpt}>
              {roleLabel(roleOpt)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleCreateUser}
          style={{
            marginLeft: 12,
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
          aria-label={t('userManagement.createUser')}
        >
          {t('userManagement.create')}
        </button>
      </div>

      {error && <div className="notify-error">{error}</div>}
      {success && <div className="notify-success">{success}</div>}

      <h3 style={{ color: '#1976d2', marginBottom: 12 }}>{t('userManagement.users')}</h3>
      <div className="table-responsive">
      <table
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          background: '#f5f8fa',
          borderRadius: 8,
          boxShadow: '0 1px 4px #0001',
        }}
      >
        <thead>
          <tr style={{ background: '#1976d2', color: '#fff' }}>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('common.username')}</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('common.email')}</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('common.role')}</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('userManagement.status')}</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('userManagement.companies')}</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>Deposits</th>
            <th style={{ padding: '10px 8px', textAlign: 'left' }}>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => {
            const assigned = assignedByUser[user.id];
            const assignedDeposits = assignedDepositsByUser[user.id];
            const expanded = expandedUserId === user.id;
            const busy = !!busyUserIds[user.id];

            return (
              <React.Fragment key={user.id}>
                <tr style={{ background: '#fff' }}>
                  <td style={{ padding: '8px 6px' }}>{user.username}</td>
                  <td style={{ padding: '8px 6px' }}>{user.email}</td>
                  <td style={{ padding: '8px 6px' }}>
                    <select
                      value={user.role}
                      onChange={e => handleChangeRole(user.id, e.target.value)}
                      style={{ padding: 6, borderRadius: 4, border: '1px solid #1976d2', minWidth: 140 }}
                    >
                      {rolesList.map(roleOpt => (
                        <option key={roleOpt} value={roleOpt}>
                          {roleLabel(roleOpt)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    {user.active === false ? (
                      <span style={{ 
                        background: '#f8d7da', 
                        color: '#721c24', 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        Inactive
                      </span>
                    ) : user.firstLogin ? (
                      <span style={{ 
                        background: '#fff3cd', 
                        color: '#856404', 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {t('userManagement.firstLogin')}
                      </span>
                    ) : (
                      <span style={{ 
                        background: '#d4edda', 
                        color: '#155724', 
                        padding: '2px 8px', 
                        borderRadius: 4, 
                        fontSize: 12,
                        fontWeight: 600
                      }}>
                        {t('userManagement.active')}
                      </span>
                    )}
                  </td>

                  {/* Companies column: show chips (if loaded) or a small hint */}
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    {Array.isArray(assigned) ? (
                      assigned.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {assigned.map(c => (
                            <CompanyChip
                              key={c.id}
                              company={c}
                              onRemove={id => handleRemoveCompany(user.id, id)}
                              t={t}
                            />
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#555' }}>No companies</span>
                      )
                    ) : (
                      <span style={{ color: '#777' }}>—</span>
                    )}
                  </td>

                  {/* Deposits column: show chips (if loaded) or a small hint */}
                  <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                    {Array.isArray(assignedDeposits) ? (
                      assignedDeposits.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {assignedDeposits.map(d => (
                            <DepositChip
                              key={d.id}
                              deposit={d}
                              onRemove={id => handleRemoveDeposit(user.id, id)}
                              t={t}
                            />
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#555' }}>No deposits</span>
                      )
                    ) : (
                      <span style={{ color: '#777' }}>—</span>
                    )}
                  </td>

                  <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => toggleManageCompanies(user.id)}
                      style={{
                        background: expanded ? '#e3f2fd' : '#1976d2',
                        color: expanded ? '#1976d2' : '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginRight: 8,
                        marginBottom: 4,
                      }}
                    >
                      {expanded ? 'Close' : 'Manage'}
                    </button>
                    <button
                      onClick={() => toggleSubscriptionPanel(user.id)}
                      style={{
                        background: showSubscriptionPanel === user.id ? '#fff3cd' : '#4caf50',
                        color: showSubscriptionPanel === user.id ? '#856404' : '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginRight: 8,
                        marginBottom: 4,
                      }}
                    >
                      {showSubscriptionPanel === user.id ? 'Close Sub' : 'Subscription'}
                    </button>
                    <button
                      onClick={() => openAdminPassModal(user)}
                      style={{
                        background: '#ff9800',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginRight: 8,
                        marginBottom: 4,
                      }}
                    >
                      {t('passwordReset.resetPassword')}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowEmailUpdateModal(true);
                      }}
                      style={{
                        background: '#2196f3',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginRight: 8,
                        marginBottom: 4,
                      }}
                    >
                      Update Email
                    </button>
                    <button
                      onClick={() => handleToggleActive(user.id, user.active !== false)}
                      style={{
                        background: user.active === false ? '#4caf50' : '#9e9e9e',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginRight: 8,
                        marginBottom: 4,
                      }}
                    >
                      {user.active === false ? 'Activate' : 'Deactivate'}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      style={{
                        background: '#000',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '6px 12px',
                        fontWeight: 600,
                        marginBottom: 4,
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>

                {/* Subscription management panel */}
                {showSubscriptionPanel === user.id && (() => {
                  const sub = subscriptionsByUser[user.id];
                  const isInfinite = !!sub?.infiniteSubscription;
                  const highlighted = highlightedUsers.has(user.id);
                  const busy = !!busyUserIds[user.id];
                  const createForm = newSubForm[user.id] ?? { plan: 'FREE', autoRenew: false };
                  return (
                    <tr key={`sub-panel-${user.id}-v${subVersions[user.id] ?? 0}`}>
                      <td colSpan={7} style={{ background: '#fffbf0', padding: '16px 10px' }}>
                        {!sub ? (
                          /* ── No subscription found ── */
                          <div>
                            <h4 style={{ color: '#f57c00', marginTop: 0, marginBottom: 8 }}>
                              Subscription — {user.username}
                            </h4>
                            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fff3e0', borderRadius: 8, border: '1px solid #ffcc80', color: '#e65100', fontSize: 13 }}>
                              ⚠ No subscription record found for this user.
                            </div>
                            <div style={{ padding: '14px 16px', background: '#f3e5f5', borderRadius: 8, border: '1px solid #ce93d8' }}>
                              <strong style={{ display: 'block', marginBottom: 10, color: '#6a1b9a', fontSize: 14 }}>Create Subscription</strong>
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, color: '#999', marginBottom: 3 }}>PLAN</label>
                                  <select
                                    value={createForm.plan}
                                    onChange={e => setNewSubForm(prev => ({ ...prev, [user.id]: { ...createForm, plan: e.target.value } }))}
                                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ce93d8', fontSize: 13 }}
                                  >
                                    {SUBSCRIPTION_PLANS.map(p => (
                                      <option key={p.key} value={p.key}>{p.label} ({p.period})</option>
                                    ))}
                                  </select>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input
                                    type="checkbox"
                                    id={`autorenew-${user.id}`}
                                    checked={createForm.autoRenew}
                                    onChange={e => setNewSubForm(prev => ({ ...prev, [user.id]: { ...createForm, autoRenew: e.target.checked } }))}
                                  />
                                  <label htmlFor={`autorenew-${user.id}`} style={{ fontSize: 13, color: '#555' }}>Auto-renew</label>
                                </div>
                                <button
                                  onClick={() => handleCreateSubscription(user.id)}
                                  disabled={busy}
                                  style={{
                                    background: '#9c27b0', color: '#fff', border: 'none', borderRadius: 6,
                                    padding: '8px 18px', fontWeight: 600, fontSize: 13,
                                    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
                                  }}
                                >
                                  {busy ? 'Creating…' : '+ Create Subscription'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h4 style={{ color: '#f57c00', marginTop: 0, marginBottom: 12 }}>
                              Subscription — {user.username}
                            </h4>

                            {/* ── Live info badges ── */}
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 10,
                              marginBottom: 16,
                              padding: '12px 14px',
                              borderRadius: 10,
                              background: highlighted ? '#fff8e1' : '#fff3e0',
                              border: `2px solid ${highlighted ? '#ffb300' : '#ffe0b2'}`,
                              transition: 'background 0.4s, border-color 0.4s',
                            }}>
                              {/* Plan */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>PLAN</span>
                                <span style={{ fontWeight: 700, fontSize: 15, color: '#e65100' }}>{String(sub.plan)}</span>
                              </div>
                              {/* Status */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>STATUS</span>
                                <span style={{
                                  fontWeight: 700, fontSize: 14,
                                  color: String(sub.status) === 'ACTIVE' ? '#2e7d32' : String(sub.status) === 'CANCELLED' ? '#c62828' : '#f57c00'
                                }}>
                                  {String(sub.status)}
                                </span>
                              </div>
                              {/* Days remaining */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>DAYS LEFT</span>
                                <span style={{
                                  fontWeight: 800, fontSize: 20,
                                  color: isInfinite ? '#6a1b9a' : Number(sub.remainingDays) <= 0 ? '#c62828' : Number(sub.remainingDays) < 7 ? '#e65100' : '#2e7d32'
                                }}>
                                  {isInfinite ? '∞' : String(sub.remainingDays)}
                                </span>
                              </div>
                              {/* End date */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>EXPIRES</span>
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                  {isInfinite ? 'Never' : (() => { try { return new Date(sub.endDate).toLocaleDateString(); } catch { return sub.endDate; } })()}
                                </span>
                              </div>
                              {/* Infinite */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>INFINITE</span>
                                <span style={{ fontWeight: 700, fontSize: 15, color: isInfinite ? '#6a1b9a' : '#9e9e9e' }}>
                                  {isInfinite ? 'YES' : 'NO'}
                                </span>
                              </div>
                              {/* Auto-renew */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>AUTO-RENEW</span>
                                <span style={{ fontWeight: 700, fontSize: 15, color: sub.autoRenew ? '#1565c0' : '#9e9e9e' }}>
                                  {sub.autoRenew ? 'ON' : 'OFF'}
                                </span>
                              </div>
                              {/* Start date */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>STARTED</span>
                                <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                  {sub.startDate ? (() => { try { return new Date(sub.startDate).toLocaleDateString(); } catch { return sub.startDate; } })() : '—'}
                                </span>
                              </div>
                              {/* Last renewal */}
                              {sub.lastRenewalDate && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 8, padding: '8px 14px' }}>
                                  <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>LAST RENEWAL</span>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                    {(() => { try { return new Date(sub.lastRenewalDate!).toLocaleDateString(); } catch { return sub.lastRenewalDate; } })()}
                                  </span>
                                </div>
                              )}
                              {/* Cancelled date */}
                              {sub.cancelledDate && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 110, background: '#fff', border: '1px solid #ffcdd2', borderRadius: 8, padding: '8px 14px' }}>
                                  <span style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>CANCELLED</span>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: '#c62828' }}>
                                    {(() => { try { return new Date(sub.cancelledDate!).toLocaleDateString(); } catch { return sub.cancelledDate; } })()}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Notes & audit info */}
                            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 12, color: '#555', display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                              {sub.notes && (
                                <span><strong>Notes:</strong> {sub.notes}</span>
                              )}
                              {sub.lastModifiedBy && (
                                <span><strong>Modified by:</strong> {sub.lastModifiedBy}</span>
                              )}
                              {sub.lastModifiedDate && (
                                <span><strong>Modified:</strong> {(() => { try { return new Date(sub.lastModifiedDate!).toLocaleString(); } catch { return sub.lastModifiedDate; } })()}</span>
                              )}
                              {!sub.notes && !sub.lastModifiedBy && !sub.lastModifiedDate && (
                                <span style={{ color: '#bbb' }}>No additional notes</span>
                              )}
                            </div>

                            {/* ── Change Plan ── */}
                            <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f3e5f5', borderRadius: 8, border: '1px solid #ce93d8' }}>
                              <strong style={{ display: 'block', marginBottom: 8, color: '#6a1b9a', fontSize: 13 }}>Change Plan:</strong>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {SUBSCRIPTION_PLANS.map(p => (
                                  <button
                                    key={p.key}
                                    onClick={() => handleUpdatePlan(user.id, p.key)}
                                    disabled={busy || String(sub.plan) === p.key}
                                    style={{
                                      background: String(sub.plan) === p.key ? p.bg : '#fff',
                                      color: p.color,
                                      border: `2px solid ${String(sub.plan) === p.key ? p.color : '#ddd'}`,
                                      borderRadius: 6,
                                      padding: '5px 12px',
                                      fontWeight: String(sub.plan) === p.key ? 700 : 500,
                                      cursor: busy || String(sub.plan) === p.key ? 'default' : 'pointer',
                                      fontSize: 13,
                                      opacity: busy && String(sub.plan) !== p.key ? 0.6 : 1,
                                    }}
                                  >
                                    {p.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* ── Adjust Days ── */}
                            <div style={{ marginBottom: 16, borderTop: '1px solid #f0ad4e', paddingTop: 14, opacity: isInfinite ? 0.45 : 1, pointerEvents: isInfinite ? 'none' : 'auto' }}>
                              <strong style={{ display: 'block', marginBottom: 6, color: '#f57c00', fontSize: 13 }}>
                                Adjust Days (current: {isInfinite ? '∞' : sub.remainingDays}):
                              </strong>
                              {isInfinite && (
                                <div style={{ marginBottom: 8, fontSize: 12, color: '#6a1b9a', fontStyle: 'italic' }}>
                                  ⚠ Remove infinite status first to adjust days.
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {[-30, -7, +7, +30, +90, +365].map(d => (
                                  <button
                                    key={d}
                                    onClick={() => handleExtendDays(user.id, d)}
                                    disabled={busy || isInfinite}
                                    style={{
                                      background: d < 0 ? '#fff3e0' : '#e8f5e9',
                                      color: d < 0 ? '#e65100' : '#2e7d32',
                                      border: `1px solid ${d < 0 ? '#ffcc80' : '#a5d6a7'}`,
                                      borderRadius: 6, padding: '6px 12px', fontWeight: 600,
                                      cursor: busy || isInfinite ? 'not-allowed' : 'pointer',
                                      opacity: busy || isInfinite ? 0.7 : 1, fontSize: 13,
                                    }}
                                  >
                                    {d > 0 ? `+${d}d` : `${d}d`}
                                  </button>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                                <input
                                  type="number"
                                  min="0"
                                  placeholder="Set exact days…"
                                  value={daysInput[user.id] || ''}
                                  onChange={e => setDaysInput(prev => ({ ...prev, [user.id]: e.target.value }))}
                                  disabled={isInfinite}
                                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #f0ad4e', width: 150, fontSize: 13 }}
                                />
                                <button
                                  onClick={() => handleSetExactDays(user.id)}
                                  disabled={busy || !daysInput[user.id]}
                                  style={{
                                    background: '#f57c00', color: '#fff', border: 'none', borderRadius: 6,
                                    padding: '6px 14px', fontWeight: 600, fontSize: 13,
                                    cursor: busy || !daysInput[user.id] ? 'not-allowed' : 'pointer',
                                    opacity: busy || !daysInput[user.id] ? 0.7 : 1,
                                  }}
                                >
                                  {busy ? 'Saving…' : 'Set Days'}
                                </button>
                              </div>
                            </div>

                            {/* ── Infinite toggle + Cancel ── */}
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                              <button
                                onClick={() => handleSetInfiniteSubscription(user.id, !isInfinite)}
                                disabled={busy}
                                style={{
                                  background: isInfinite ? '#f44336' : '#9c27b0', color: '#fff',
                                  border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600,
                                  cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
                                }}
                              >
                                {isInfinite ? '✕ Remove Infinite' : '∞ Set Infinite'}
                              </button>
                              <button
                                onClick={() => handleCancelSubscription(user.id)}
                                disabled={busy || String(sub.status) === 'CANCELLED'}
                                style={{
                                  background: String(sub.status) === 'CANCELLED' ? '#ccc' : '#f44336',
                                  color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px',
                                  fontWeight: 600,
                                  cursor: busy || String(sub.status) === 'CANCELLED' ? 'not-allowed' : 'pointer',
                                  opacity: busy ? 0.7 : 1,
                                }}
                              >
                                {String(sub.status) === 'CANCELLED' ? '✕ Cancelled' : '✕ Cancel Subscription'}
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })()}

                {/* Expanded row with assign/remove controls */}
                {expanded && (
                  <tr>
                    <td colSpan={7} style={{ background: '#f9fbff', padding: '12px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <strong style={{ color: '#1976d2' }}>Add company:</strong>
                        <select
                          value={rowSelect[user.id] ?? ''}
                          onChange={e =>
                            setRowSelect(prev => ({
                              ...prev,
                              [user.id]: e.target.value ? Number(e.target.value) : '',
                            }))
                          }
                          style={{
                            padding: 8,
                            borderRadius: 6,
                            border: '1px solid #1976d2',
                            minWidth: 220,
                          }}
                        >
                          <option value="">Select company…</option>
                          {companies
                            .filter(c => !(assigned || []).some(a => a.id === c.id))
                            .map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.location ? ` (${c.location})` : ''}
                              </option>
                            ))}
                        </select>
                        <button
                          disabled={busy || !rowSelect[user.id]}
                          onClick={() => handleAssignCompany(user.id)}
                          style={{
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontWeight: 600,
                            opacity: busy || !rowSelect[user.id] ? 0.7 : 1,
                          }}
                        >
                          {busy ? 'Working…' : 'Add'}
                        </button>

                        {/* If you want, add a refresh button */}
                        <button
                          onClick={() => loadUserCompanies(user.id)}
                          disabled={busy}
                          style={{
                            background: 'transparent',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontWeight: 600,
                          }}
                        >
                          Refresh
                        </button>
                      </div>

                      {/* Already assigned list duplicated here for convenience */}
                      <div style={{ marginTop: 10 }}>
                        <strong style={{ color: '#1976d2' }}>Assigned:</strong>
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap' }}>
                          {(assigned || []).length
                            ? (assigned || []).map(c => (
                                <CompanyChip
                                  key={c.id}
                                  company={c}
                                  onRemove={id => handleRemoveCompany(user.id, id)}
                                  t={t}
                                />
                              ))
                            : <span style={{ marginLeft: 8, color: '#555' }}>No companies</span>}
                        </div>
                      </div>

                      {/* Deposits management */}
                      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px dashed #cfe0ff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <strong style={{ color: '#1976d2' }}>Add deposit:</strong>
                          <select
                            value={rowDepositCompanySelect[user.id] ?? ''}
                            onChange={e => {
                              const companyId = e.target.value ? Number(e.target.value) : '';
                              setRowDepositCompanySelect(prev => ({ ...prev, [user.id]: companyId }));
                              setRowDepositSelect(prev => ({ ...prev, [user.id]: '' }));
                            }}
                            style={{
                              padding: 8,
                              borderRadius: 6,
                              border: '1px solid #1976d2',
                              minWidth: 220,
                            }}
                            disabled={!assigned || assigned.length === 0}
                          >
                            <option value="">Select company context…</option>
                            {(assigned || []).map(c => (
                              <option key={c.id} value={c.id}>
                                {c.name}{c.location ? ` (${c.location})` : ''}
                              </option>
                            ))}
                          </select>
                          <select
                            value={rowDepositSelect[user.id] ?? ''}
                            onChange={e =>
                              setRowDepositSelect(prev => ({
                                ...prev,
                                [user.id]: e.target.value ? Number(e.target.value) : '',
                              }))
                            }
                            style={{
                              padding: 8,
                              borderRadius: 6,
                              border: '1px solid #1976d2',
                              minWidth: 220,
                            }}
                            disabled={!assigned || assigned.length === 0}
                          >
                            <option value="">Select deposit…</option>
                            {getDepositsForSelectedCompany(user.id)
                              .filter(d => !(assignedDeposits || []).some(ad => ad.id === d.id))
                              .map(d => (
                                <option key={d.id} value={d.id}>
                                  {d.name}{d.location ? ` (${d.location})` : ''}
                                </option>
                              ))}
                          </select>
                          <button
                            disabled={busy || !rowDepositSelect[user.id] || !assigned || assigned.length === 0 || !rowDepositCompanySelect[user.id]}
                            onClick={() => handleAssignDeposit(user.id)}
                            style={{
                              background: '#1976d2',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              padding: '6px 12px',
                              fontWeight: 600,
                              opacity: busy || !rowDepositSelect[user.id] || !assigned || assigned.length === 0 || !rowDepositCompanySelect[user.id] ? 0.7 : 1,
                            }}
                          >
                            {busy ? 'Working…' : 'Add'}
                          </button>
                          <button
                            onClick={() => loadUserDeposits(user.id)}
                            disabled={busy}
                            style={{
                              background: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: 6,
                              padding: '6px 12px',
                              fontWeight: 600,
                            }}
                          >
                            Refresh deposits
                          </button>
                        </div>

                        {(!assigned || assigned.length === 0) && (
                          <div style={{ marginTop: 6, color: '#ff9800', fontSize: 12, fontStyle: 'italic' }}>
                            ⚠️ User must be assigned to a company before adding deposits
                          </div>
                        )}

                        <div style={{ marginTop: 10 }}>
                          <strong style={{ color: '#1976d2' }}>Assigned deposits:</strong>
                          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap' }}>
                            {(assignedDeposits || []).length
                              ? (assignedDeposits || []).map(d => (
                                  <DepositChip
                                    key={d.id}
                                    deposit={d}
                                    onRemove={id => handleRemoveDeposit(user.id, id)}
                                    t={t}
                                  />
                                ))
                              : <span style={{ marginLeft: 8, color: '#555' }}>No deposits</span>}
                          </div>
                        </div>
                      </div>

                      {/* If the endpoint /users/:id/companies doesn't exist yet */}
                      {!assigned && (
                        <div style={{ marginTop: 8, color: '#777', fontSize: 13 }}>
                          Tip: implement <code>GET /users/{'{userId}'}/companies</code> to show current assignments.
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* Admin password reset — input modal */}
      {showAdminPassModal && adminPassUser && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 32, minWidth: 400,
            maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>Reset Password</h3>
            <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
              Set a new password for <strong>{adminPassUser.username}</strong>.
              Edit the field below or click <em>Regenerate</em> for a new random one.
            </p>

            {/* Password input row */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type={adminPassVisible ? 'text' : 'password'}
                  value={adminPassValue}
                  onChange={e => setAdminPassValue(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 40px 10px 12px', fontSize: 15,
                    border: '1px solid #ccc', borderRadius: 6, boxSizing: 'border-box',
                    fontFamily: 'monospace'
                  }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setAdminPassVisible(v => !v)}
                  style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                    color: '#888', padding: 0
                  }}
                  title={adminPassVisible ? 'Hide' : 'Show'}
                >
                  {adminPassVisible ? '🙈' : '👁'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setAdminPassValue(generatePassword(12))}
                style={{
                  background: '#f0f0f0', border: '1px solid #ccc', borderRadius: 6,
                  padding: '10px 14px', cursor: 'pointer', fontWeight: 600,
                  fontSize: 13, whiteSpace: 'nowrap'
                }}
                title="Generate a random password"
              >
                🔄 Regenerate
              </button>
            </div>

            {adminPassError && (
              <p style={{ color: '#d32f2f', fontSize: 13, margin: '4px 0 12px' }}>{adminPassError}</p>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={submitAdminResetPassword}
                disabled={adminPassLoading}
                style={{
                  background: adminPassLoading ? '#aaa' : '#ff9800',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '9px 22px', fontWeight: 700, cursor: adminPassLoading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                {adminPassLoading ? 'Applying...' : 'Apply'}
              </button>
              <button
                onClick={() => { setShowAdminPassModal(false); setAdminPassError(''); }}
                disabled={adminPassLoading}
                style={{
                  background: '#e0e0e0', color: '#333', border: 'none',
                  borderRadius: 6, padding: '9px 22px', fontWeight: 600,
                  cursor: 'pointer', fontSize: 14
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result modal — show set password with copy button */}
      {showGeneratedPasswordModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: 10, padding: 32, minWidth: 380,
            maxWidth: 480, boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>✅ Password Reset Successfully</h3>
            <p style={{ color: '#555', marginBottom: 16, fontSize: 14 }}>
              The password has been updated. Share it with the user — they should change it after logging in.
            </p>
            <div style={{
              background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6,
              padding: '12px 16px', fontFamily: 'monospace', fontSize: 18,
              letterSpacing: 2, textAlign: 'center', marginBottom: 16, color: '#222'
            }}>
              {generatedPassword}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { navigator.clipboard.writeText(generatedPassword); setCopiedPassword(true); }}
                style={{
                  background: copiedPassword ? '#4caf50' : '#1976d2',
                  color: '#fff', border: 'none', borderRadius: 6,
                  padding: '8px 18px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                {copiedPassword ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={() => { setShowGeneratedPasswordModal(false); setGeneratedPassword(''); }}
                style={{
                  background: '#e0e0e0', color: '#333', border: 'none',
                  borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <EmailUpdateModal
        isOpen={showEmailUpdateModal}
        onClose={() => {
          setShowEmailUpdateModal(false);
          setSelectedUser(null);
        }}
        userId={selectedUser?.id || 0}
        currentEmail={selectedUser?.email}
        isOwnProfile={false}
      />
    </div>
  );
}
